/**
 * @fileoverview Module for `src/lib/queries/queries.js`.
 */

/**
 * React Query hooks for frontend API data access and mutations.
 *
 * Centralizes query key generation, cache invalidation behavior, and shared
 * optimistic update logic for voter, letter bundle, and user flows.
 */

import { useMutation, useQuery, useQueryClient } from 'react-query';
import { segmentTrack } from '../segment';

import logger from '../logger';
import { getUserId } from '../user';
import { getWithOptionalToken, getWithToken, patchWithToken, postWithToken } from '../apiClient';
import { letterKitType } from '../../letter-kits/letterKitTypes';
import { useAuth } from '../../contexts/AuthContext';
import { useState, useRef } from 'react';
import downloadLetters from '../downloadLetters';
import downloadLetterBundles from '../downloadLetterBundles';

const getLettersQueryFields = [
  'id',
  'election_date',
  'toPrep',
  'toSend',
  'alreadySent',
  'letter_bundle_id',
  'letter_bundle_type',
  'letter_bundle_status',
  'mail_date_from',
  'mail_date',
  'first_name',
  'last_name',
  'city',
  'state',
].join(',');

/**
 * Canonical React Query keys used across list/detail queries and invalidations.
 */
const queryKeys = {
  campaigns: {
    get: ['campaigns', 'get'],
  },
  letterBundleCounts: {
    get: ['letters', 'counts'],
  },
  letterBundles: {
    get: (status, offsetData, searchFilter = '') => [
      'letters',
      'status',
      status,
      offsetData.timestamp || 'no-timestamp',
      offsetData.bundleId || 'no-bundleId',
      (searchFilter && (searchFilter !== '')) ? searchFilter : 'no-filter',
    ],
  },
  letterKits: {
    get: (params = {}) => ['letterKits', 'get', Object.keys(params).length > 0 ? params : null].filter(Boolean),
  },
  letters: {
    get: ['letters', 'get'],
  },
  users: {
    self: {
      get: ['users', 'get', { userId: 'self' }],
    },
  },
};

/**
 * Shared selector functions used by query `select` handlers.
 */
const selectHelpers = {
  compareVotersByName: (a, b) => `${ a.first_name } ${ a.last_name }`.localeCompare(`${ b.first_name } ${ b.last_name }`),
};

/**
 * Fetches campaign/district data with optional authenticated context.
 *
 * @param {Object} [params={}] - Query params forwarded to `/get-districts`.
 * @param {Object} [options={}] - React Query options.
 * @returns {Object} React Query result for campaigns request.
 */
function useCampaigns(params = {}, options = {}) {
  const auth = useAuth();
  return useQuery(
    [...queryKeys.campaigns.get, Object.keys(params).length > 0 ? params : null].filter(Boolean),
    async () => await getWithOptionalToken(
      '/get-districts',
      Object.prototype.hasOwnProperty.call(params, 'auth0Id') ? null : auth,
      { params },
    ),
    options,
  );
}

/**
 * Builds download state handlers for downloading an individual letter.
 *
 * @param {Object} params
 * @param {string|number} params.letterId - Letter identifier to download.
 * @param {Object} params.modifyAndDownloadState - Shared lock state for UI actions.
 * @returns {Object} Download state and `startDownload` action.
 */
function useDownloadLetterState({ letterId, modifyAndDownloadState }) {
  const auth = useAuth();
  const { data: user } = useUser({
    placeholderData: { auth0_id: getUserId() }, // FIXME: This is transitional while we move to a more-query based system
  });

  return useDownloadState({
    downloadId: `letter-${ letterId }`,
    downloadFunction: () => downloadLetters({
      auth,
      userId: user.auth0_id,
      letterId,
      excludePrepped: false,
    }),
    modifyAndDownloadState,
  });
}

/**
 * Builds download state handlers for downloading a letter bundle.
 *
 * @param {Object} params
 * @param {string|number} params.letterBundleId - Bundle identifier to download.
 * @param {Object} params.modifyAndDownloadState - Shared lock state for UI actions.
 * @returns {Object} Download state and `startDownload` action.
 */
function useDownloadBundleState({ letterBundleId, modifyAndDownloadState }) {
  const auth = useAuth();
  const { data: user } = useUser({
    placeholderData: { auth0_id: getUserId() }, // FIXME: This is transitional while we move to a more-query based system
  });

  return useDownloadState({
    downloadId: `bundle-${ letterBundleId }`,
    downloadFunction: () => downloadLetterBundles({
      filters: { letterBundleIds: [letterBundleId] },
      auth,
      userId: user.auth0_id,
      excludePrepped: true,
    }),
    modifyAndDownloadState,
  });
}

/**
 * Tracks whether modify/download actions should be disabled in the UI.
 *
 * `lockDownload`/`unlockDownload` keep a running counter so multiple concurrent
 * downloads do not unlock the UI early.
 *
 * @returns {Object} Disable flags and lock/unlock actions.
 */
function useModifyAndDownloadState() {
  const [isModifyInProgress, setIsModifyInProgress] = useState(false);
  const [isAnyDownloadInProgress, setIsAnyDownloadInProgress] = useState(false);
  const totalDownloadsInProgress = useRef(0);
  return {
    isModifyDisabled: isModifyInProgress || isAnyDownloadInProgress,
    isDownloadDisabled: isModifyInProgress,
    lockModify: () => setIsModifyInProgress(true),
    lockDownload: () => {
      totalDownloadsInProgress.current += 1;
      return setIsAnyDownloadInProgress(true);
    },
    unlockModify: () => setIsModifyInProgress(false),
    unlockDownload: () => {
      totalDownloadsInProgress.current -= 1;
      if (totalDownloadsInProgress.current === 0) {
        return setIsAnyDownloadInProgress(false);
      }
    },
  };
}

/**
 * Generic download state hook keyed by a download identifier.
 *
 * @param {Object} params
 * @param {string} params.downloadId - Unique download key.
 * @param {Function} params.downloadFunction - Async function that performs download.
 * @param {Object} params.modifyAndDownloadState - Shared lock state for UI actions.
 * @returns {Object} Download booleans and `startDownload` action.
 */
function useDownloadState({ downloadId, downloadFunction, modifyAndDownloadState }) {
  const [downloading, setDownloading] = useState({});
  const [downloadSuccess, setDownloadSuccess] = useState({});
  const [downloadError, setDownloadError] = useState({});

  return {
    downloading: downloading[downloadId],
    downloadSuccess: downloadSuccess[downloadId],
    downloadError: downloadError[downloadId],
    startDownload: async () => {
      await Promise.all([
        modifyAndDownloadState.lockDownload(),
        setDownloading({ ...downloading, [downloadId]: true }),
        setDownloadSuccess({ ...downloadSuccess, [downloadId]: false }),
        setDownloadError({ ...downloadError, [downloadId]: false }),
      ]);
      try {
        await downloadFunction();
        await Promise.all([
          modifyAndDownloadState.unlockDownload(),
          setDownloading({ ...downloading, [downloadId]: false }),
          setDownloadSuccess({ ...downloadSuccess, [downloadId]: true }),
        ]);
        segmentTrack('LettersDownload:success', { downloadId });
      } catch {
        await Promise.all([
          modifyAndDownloadState.unlockDownload(),
          setDownloading({ ...downloading, [downloadId]: false }),
          setDownloadError({ ...downloadError, [downloadId]: true }),
        ]);
      }
    },
  };
}

/**
 * Creates mutation for sending a reminder email to a social bundle assignee.
 *
 * @param {Object} params
 * @param {string} params.socialToken - Social bundle token.
 * @returns {Object} React Query mutation object.
 */
function useSendSocialReminderMutation({ socialToken }) {
  const auth = useAuth();
  return useMutation(
    async () => await postWithToken(
      `/v1/socialBatches/bundles/${ socialToken }/reminder`,
      {},
      auth,
    ),
  );
}

/**
 * Creates mutation for creating a social batch for the current user.
 *
 * @param {Object} params
 * @param {Function} params.onSuccess - Callback invoked on mutation success.
 * @returns {Object} React Query mutation object.
 */
function useSocialBatchMutation({ onSuccess }) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { data: user } = useUser({
    placeholderData: { auth0_id: getUserId() }, // FIXME: This is transitional while we move to a more-query based system
  });
  return useMutation(
    async (formData) => await postWithToken(
      `/v1/users/${ user.auth0_id }/socialBatches`,
      formData,
      auth,
    ),
    {
      onSuccess,
      onSettled: async () => {
        await Promise.all([
          queryClient.invalidateQueries(queryKeys.letterBundleCounts.get),
          queryClient.invalidateQueries(['letters', 'status', 'UNPREPPED']),
        ]);
      },
    },
  );
}

/**
 * Creates mutation for creating a standard letter bundle for the current user.
 *
 * @param {Object} params
 * @param {Function} params.onSuccess - Callback invoked on mutation success.
 * @returns {Object} React Query mutation object.
 */
function useLetterBundleMutation({ onSuccess }) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { data: user } = useUser({
    placeholderData: { auth0_id: getUserId() }, // FIXME: This is transitional while we move to a more-query based system
  });
  return useMutation(
    async (formData) => await postWithToken(
      `/v1/users/${ user.auth0_id }/letterBundles`,
      formData,
      auth,
    ),
    {
      onSuccess,
      onSettled: async () => {
        await Promise.all([
          queryClient.invalidateQueries(queryKeys.letterBundleCounts.get),
          queryClient.invalidateQueries(['letters', 'status', 'UNPREPPED']),
        ]);
      },
    },
  );
}

/**
 * Creates mutation for requesting a letter kit.
 *
 * @param {Object} [options={}] - React Query mutation options.
 * @returns {Object} React Query mutation object.
 */
function useLetterKitMutation(options = {}) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { data: user } = useUser({
    placeholderData: { auth0_id: getUserId() }, // FIXME: This is transitional while we move to a more-query based system
  });
  return useMutation(
    async (formData) => await postWithToken(
      `/v1/users/${ user.auth0_id }/letterBundles`,
      formData,
      auth,
    ),
    {
      onSettled: async () => {
        await queryClient.invalidateQueries(['letterKits', 'get']);
      },
      ...options,
    },
  );
}

/**
 * Fetches current bundle counts for dashboard summary cards.
 *
 * @returns {Object} React Query result with bundle count totals.
 */
function useBundleDataCounts() {
  const auth = useAuth();
  const { data: user } = useUser({
    placeholderData: { auth0_id: getUserId() }, // FIXME: This is transitional while we move to a more-query based system
  });

  return useQuery(
    queryKeys.letterBundleCounts.get,
    async () => await getWithToken(
      `/v1/users/${ user.auth0_id }/bundle-counts`,
      auth,
    ),
    {
      // Only auto refresh after 30 seconds. Refreshes will still be forced by invalidated queries.
      staleTime: 30000,
    },
  );
}

/**
 * Fetches paginated bundle data for a given bundle status.
 *
 * @param {Object} params
 * @param {string} params.status - Bundle status filter.
 * @param {Object} [params.offsetData={ timestamp: null, bundleId: null }] - Keyset pagination cursor.
 * @param {string} [params.letterFilter=''] - Search filter string.
 * @returns {Object} React Query result with paginated bundle data.
 */
function useBundleData({ status, offsetData = { timestamp: null, bundleId: null }, letterFilter = '' }) {
  // NEVER SET TO LESS THAN 500. 500 IS THE MAX BUNDLE SIZE, AND SETTING TO LESS CAN CAUSE ERRORS.
  const MAX_VOTERS_PER_PAGE = 1000;

  const auth = useAuth();
  const { data: user } = useUser({
    placeholderData: { auth0_id: getUserId() }, // FIXME: This is transitional while we move to a more-query based system
  });

  const offsetQueryFields = (offsetData.timestamp && offsetData.bundleId)
    ? `&offset_timestamp=${ encodeURIComponent(offsetData.timestamp) }&offset_bundle_id=${ encodeURIComponent(offsetData.bundleId) }`
    : '';

  const queryString = `?bundle_status=${ encodeURIComponent(status) }&search_filter=${ encodeURIComponent(letterFilter) }&voter_page_limit=${ MAX_VOTERS_PER_PAGE }${ offsetQueryFields }`;
  return useQuery(
    queryKeys.letterBundles.get(status, offsetData, letterFilter),
    async () => await getWithToken(
      `/v1/users/${ user.auth0_id }/bundle-data${ queryString }`,
      auth,
    ),
    {
      // Only auto refresh after 5 min. Refreshes will still be forced by invalidated queries.
      staleTime: 300000,
    },
  );
}

/**
 * Fetches letter kits for the current user.
 *
 * @param {Object} [params={}] - API filter/parameter object.
 * @param {Object} [options={}] - React Query options.
 * @returns {Object} React Query result with sorted letter kits.
 */
function useLetterKits(params = {}, options = {}) {
  const auth = useAuth();
  const { data: user } = useUser({
    placeholderData: { auth0_id: getUserId() }, // FIXME: This is transitional while we move to a more-query based system
  });
  return useQuery(
    queryKeys.letterKits.get(params),
    async () => await getWithToken(
      `/v1/users/${ user.auth0_id }/letterBundles`,
      auth,
      {
        params: {
          ...params,
          filter: {
            'createdAt:afterDaysAgo': 30,
            type: letterKitType,
            ...params.filter,
          },
        },
      },
    ),
    {
      select: (kits) => [...kits]
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
      ...options,
    },
  );
}

/**
 * Creates mutation for updating an individual letter with optimistic cache update.
 *
 * @returns {Object} React Query mutation object.
 */
function useLetterMutation() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { data: user } = useUser({
    placeholderData: { auth0_id: getUserId() }, // FIXME: This is transitional while we move to a more-query based system
  });
  return useMutation(
    async ({ filters, update, voterId }) => await patchWithToken(
      `/v1/users/${ user.auth0_id }/letters`,
      {
        filters: {
          ...filters,
          voterId,
        },
        update,
      },
      auth,
    ),
    {
      onError: (error, _, { unmutatedVoters }) => {
        logger.error(error);
        queryClient.setQueryData(queryKeys.letters.get, unmutatedVoters);
      },
      onMutate: async ({ optimisticUpdate, voterId }) => {
        await queryClient.cancelQueries(queryKeys.letters.get);
        const unmutatedVoters = queryClient.getQueryData(queryKeys.letters.get);
        const voterToMutateIndex = unmutatedVoters?.findIndex((voter) => voter.id === voterId);
        if (voterToMutateIndex >= 0) {
          const voterToMutate = unmutatedVoters[voterToMutateIndex];
          queryClient.setQueryData(
            queryKeys.letters.get,
            [
              ...unmutatedVoters.slice(0, voterToMutateIndex),
              {
                ...voterToMutate,
                ...optimisticUpdate,
              },
              ...unmutatedVoters.slice(voterToMutateIndex + 1),
            ],
          );
        }
        return { unmutatedVoters };
      },
      onSettled: async () => {
        await queryClient.invalidateQueries(queryKeys.letters.get[0]);
      },
    },
  );
}

/**
 * Fetches a specific list of letters by ID for the current user.
 *
 * @param {Object} params
 * @param {Array<string|number>} params.letterIds - Letter IDs to fetch.
 * @param {Object} [options={}] - React Query options.
 * @returns {Object} React Query result with sorted letters.
 */
function useLetters({ letterIds }, options = {}) {
  const auth = useAuth();
  const { data: user } = useUser({
    placeholderData: { auth0_id: getUserId() }, // FIXME: This is transitional while we move to a more-query based system
  });
  return useQuery(
    queryKeys.letters.get,
    async () => await getWithToken(
      `/v1/users/${ user.auth0_id }/letters`,
      auth,
      {
        params: {
          campaignTimeframe: 'active',
          fields: getLettersQueryFields,
          letterIds: letterIds.join(','),
        },
      },
    ),
    {
      enabled: letterIds?.length >= 1,
      placeholderData: [],
      select: (allLetters) => [...allLetters].sort(selectHelpers.compareVotersByName),
      ...options,
    },
  );
}

/**
 * Creates mutation for bulk-updating letters with optimistic cache updates.
 *
 * @param {Object} params
 * @param {string} [params.sourceStatus=''] - Source status cache key to invalidate.
 * @param {string} [params.targetStatus=''] - Target status cache key to invalidate.
 * @param {Object} params.modifyAndDownloadState - Shared lock state for UI actions.
 * @param {Object} params.filterOptions - Filter options including selected letters.
 * @param {Object} [params.update={}] - Partial update payload applied server-side.
 * @param {Object} [params.optimisticUpdate={}] - Partial optimistic update payload.
 * @returns {Object} React Query mutation object.
 */
function useLettersMutation({ sourceStatus = '', targetStatus = '', modifyAndDownloadState, filterOptions, update = {}, optimisticUpdate = {}}) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { data: user } = useUser({
    placeholderData: { auth0_id: getUserId() }, // FIXME: This is transitional while we move to a more-query based system
  });
  const { letters: voters, ...otherFilterOptions } = filterOptions;
  return useMutation(
    async () => {
      const voterIds = voters.map((voter) => voter.id);

      return await patchWithToken(
        `/v1/users/${ user.auth0_id }/letters`,
        {
          filters: { ...otherFilterOptions, voterIds },
          update,
        },
        auth,
      );
    },
    {
      onError: (error, _, { unmutatedVoters }) => {
        logger.error(error);
        queryClient.setQueryData(queryKeys.letters.get[0], unmutatedVoters);
      },
      onMutate: async () => {
        await Promise.all([
          modifyAndDownloadState.lockModify(),
          queryClient.cancelQueries([queryKeys.letters.get[0]]),
        ]);
        const unmutatedVoters = queryClient.getQueryData(queryKeys.letters.get[0]);
        queryClient.setQueryData(
          queryKeys.letters.get[0],
          () => (unmutatedVoters || []).map((unmutatedVoter) => ({
            ...unmutatedVoter,
            ...optimisticUpdate,
          })),
        );
        return { unmutatedVoters };
      },
      onSettled: async () => {
        await Promise.all([
          queryClient.invalidateQueries(['letters', 'status', sourceStatus]),
          queryClient.invalidateQueries(['letters', 'status', targetStatus]),
          queryClient.invalidateQueries(['letters', 'counts']),
        ]);
        await modifyAndDownloadState.unlockModify();
      },
    },
  );
}

/**
 * Fetches the current user profile.
 *
 * @param {Object} [options={}] - React Query options.
 * @returns {Object} React Query result for `/v1/users/self`.
 */
function useUser(options = {}) {
  const auth = useAuth();
  return useQuery(
    queryKeys.users.self.get,
    async () => await getWithToken('/v1/users/self', auth),
    { ...options },
  );
}

export {
  queryKeys,
  selectHelpers,
  useBundleData,
  useBundleDataCounts,
  useCampaigns,
  useDownloadBundleState,
  useDownloadLetterState,
  useLetterBundleMutation,
  useLetterKitMutation,
  useLetterKits,
  useLetterMutation,
  useLetters,
  useLettersMutation,
  useModifyAndDownloadState,
  useSendSocialReminderMutation,
  useSocialBatchMutation,
  useUser,
};
